import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { Button } from "@/components/ui/button";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link2, Undo, Redo } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Lightweight TipTap wrapper. Emits onChange with HTML string.
 * - `plainText` mode strips formatting and emits raw text.
 */
export const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Start typing…",
  plainText = false,
  testId = "rich-text-editor",
  minHeight = 120,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: plainText ? false : { levels: [2, 3] },
        bulletList: plainText ? false : {},
        orderedList: plainText ? false : {},
        bold: plainText ? false : {},
        italic: plainText ? false : {},
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // Disable StarterKit's bundled Link so our custom Link extension is the only one.
        link: false,
      }),
      Placeholder.configure({ placeholder }),
      ...(plainText ? [] : [Link.configure({ openOnClick: false, autolink: true })]),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none px-3 py-2",
          plainText && "whitespace-pre-wrap",
        ),
        "data-testid": testId,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      if (plainText) {
        onChange?.(ed.getText());
      } else {
        onChange?.(html);
      }
    },
  });

  // Keep editor content in sync when `value` changes externally (e.g. after load).
  useEffect(() => {
    if (!editor) return;
    const current = plainText ? editor.getText() : editor.getHTML();
    if (current !== (value || "")) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="border rounded-md bg-muted/30" style={{ minHeight }} />
    );
  }

  const addLink = () => {
    const url = window.prompt("URL:");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border rounded-md overflow-hidden" data-testid={`${testId}-wrap`}>
      {!plainText && (
        <div className="flex flex-wrap items-center gap-1 border-b bg-muted/40 px-2 py-1">
          <Button type="button" size="sm" variant={editor.isActive("bold") ? "default" : "ghost"} onClick={() => editor.chain().focus().toggleBold().run()} className="h-7 w-7 p-0" data-testid={`${testId}-bold`}><Bold className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="sm" variant={editor.isActive("italic") ? "default" : "ghost"} onClick={() => editor.chain().focus().toggleItalic().run()} className="h-7 w-7 p-0" data-testid={`${testId}-italic`}><Italic className="h-3.5 w-3.5" /></Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button type="button" size="sm" variant={editor.isActive("heading", { level: 2 }) ? "default" : "ghost"} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className="h-7 w-7 p-0" data-testid={`${testId}-h2`}><Heading2 className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="sm" variant={editor.isActive("heading", { level: 3 }) ? "default" : "ghost"} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className="h-7 w-7 p-0" data-testid={`${testId}-h3`}><Heading3 className="h-3.5 w-3.5" /></Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button type="button" size="sm" variant={editor.isActive("bulletList") ? "default" : "ghost"} onClick={() => editor.chain().focus().toggleBulletList().run()} className="h-7 w-7 p-0" data-testid={`${testId}-ul`}><List className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="sm" variant={editor.isActive("orderedList") ? "default" : "ghost"} onClick={() => editor.chain().focus().toggleOrderedList().run()} className="h-7 w-7 p-0" data-testid={`${testId}-ol`}><ListOrdered className="h-3.5 w-3.5" /></Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button type="button" size="sm" variant={editor.isActive("link") ? "default" : "ghost"} onClick={addLink} className="h-7 w-7 p-0" data-testid={`${testId}-link`}><Link2 className="h-3.5 w-3.5" /></Button>
          <div className="flex-1" />
          <Button type="button" size="sm" variant="ghost" onClick={() => editor.chain().focus().undo().run()} className="h-7 w-7 p-0"><Undo className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => editor.chain().focus().redo().run()} className="h-7 w-7 p-0"><Redo className="h-3.5 w-3.5" /></Button>
        </div>
      )}
      <EditorContent editor={editor} style={{ minHeight }} />
    </div>
  );
};

export default RichTextEditor;
